import { FC, useCallback } from 'react';
import dayjs from 'dayjs';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Typography } from '@altermaind/web/ui';
import { Charts } from '@/_shared/components/Charts';
import { ReadMoreTypography } from '@/_shared/components/ReadMoreTypography';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import {
  getSurveysCampaignsCall,
  getTemplatesCall,
  ResponsePostEmployerByIdSurveyResult,
} from '@/surveys/state/api';
import { ResultsNotVisibleForEmployee } from '../ResultsNotVisible';
import { SurveyEmployeeResultsVsTeamVsDepartment } from './SurveyEmployeeResultsVsTeamVsDepartment';

type Props = {
  survey: ResponsePostEmployerByIdSurveyResult[0];
};

export const SurveyCompleted: FC<Props> = ({ survey }) => {
  const t = useTranslations('department.details.assessment');

  const { genericResponsiveValue } = useResponsiveStyle();

  const { response: responseGetSurveysCampaigns } = getSurveysCampaignsCall.hook();
  const { response: responseGetTemplates } = getTemplatesCall.hook();

  const getDescriptionMetric = useCallback(
    (surveyTemplateId: string, metricName: string) => {
      if (!responseGetTemplates) {
        return '';
      }
      return (
        responseGetTemplates
          ?.find(template => template.id === surveyTemplateId)
          ?.metrics?.find(metric => metric.name === metricName)?.description ?? ''
      );
    },
    [responseGetTemplates],
  );

  return (
    <Box mb='m'>
      <Box display='flex' justifyContent='space-between' alignContent='center'>
        <Box
          alignItems={genericResponsiveValue({
            breakPoints: { desktop: 'center' },
            defaultValue: 'flex-start',
          })}
          display='flex'
          flexDirection={genericResponsiveValue({
            breakPoints: { desktop: 'row' },
            defaultValue: 'column',
          })}
        >
          <Typography variant='h2'>{survey.templateName}</Typography>
          <Box
            display='flex'
            flexDirection={genericResponsiveValue({
              breakPoints: { desktop: 'row' },
              defaultValue: 'row',
            })}
            mt={genericResponsiveValue({
              breakPoints: { desktop: 'none' },
              defaultValue: 'xs',
            })}
          >
            <Box
              ml={genericResponsiveValue({
                breakPoints: { desktop: 'xs' },
                defaultValue: 'none',
              })}
              width='s'
              height='s'
              borderRadius='l'
              backgroundColor={survey.completed ? 'success' : 'warning'}
            />
            <Typography ml='xxxs' variant='p2/light'>
              {t('status.completed', {
                date: dayjs(survey.results?.completedAt).format('DD/MM/YYYY'),
              })}
            </Typography>
          </Box>
        </Box>
      </Box>
      {survey.canViewResults ? (
        <>
          <Box
            mt='s'
            display='flex'
            flexDirection={genericResponsiveValue({
              breakPoints: { desktop: 'row' },
              defaultValue: 'column',
            })}
            alignItems='flex-start'
            mb='s'
          >
            <Box flex='2'>
              <Typography mb='xs' variant='h3'>
                {t('genericSurveyResults.titleAverage')}
              </Typography>
              <Typography variant='p3/light' mr='s' mb='l'>
                {t('descriptionCompleted', {
                  metricsCount:
                    (survey &&
                      survey.results &&
                      survey.results.results &&
                      survey.results?.results.length) ??
                    0,
                  surveyTemplateName: survey.templateName,
                })}
              </Typography>
              <Charts.RadarChartComponent
                data={(survey.results?.results ?? []).map(metric => {
                  return {
                    subject: metric.metric?.name ?? '',
                    current: metric.score ?? 0,
                    expected: 7,
                    description: getDescriptionMetric(
                      survey.templateId || '',
                      metric.metric?.name || '',
                    ),
                  };
                })}
              />
            </Box>
            <Box flex='1'>
              <Card>
                <Typography variant='h4'>{t('genericSurveyResults.insightBox.title')}</Typography>
                <Box display='flex' flexDirection='column' mb='m' mt='xs'>
                  <ReadMoreTypography variant='p3/light'>
                    {Object.keys(survey.results?.insights ?? {}).map(key => (
                      <Typography variant='p3/label' key={key}>
                        {key} -{' '}
                        <Typography as='span' variant='p3/light'>
                          {(survey.results?.insights as Record<string, string>)[key]}
                        </Typography>
                        {'\n'}
                      </Typography>
                    ))}
                  </ReadMoreTypography>
                </Box>
                <Typography variant='h4'>
                  {t('genericSurveyResults.insightBox.proTitle')}
                </Typography>
                <Box display='flex' flexDirection='column' mb='m' mt='xs'>
                  {survey.results?.results?.map(item => {
                    const maxScore = Math.max(
                      ...(survey.results?.results ?? []).map(m => m.score ?? 0),
                    );
                    if (item.score === maxScore) {
                      return (
                        <Card
                          mb='xxs'
                          backgroundColor='primary/inactive'
                          key={item.metric?.name}
                          px='xs'
                          py='xxxs'
                          width='fit-content'
                        >
                          <Typography variant='p2/light' fontSize='xs'>
                            {item.metric?.name}
                          </Typography>
                        </Card>
                      );
                    }
                    return null;
                  })}
                </Box>
                <Typography variant='h4'>
                  {t('genericSurveyResults.insightBox.conTitle')}
                </Typography>
                <Box display='flex' flexDirection='column' mt='xs'>
                  {survey.results?.results?.map(item => {
                    const minScore = Math.min(
                      ...(survey.results?.results ?? []).map(m => m.score ?? 0),
                    );
                    if (item.score === minScore) {
                      return (
                        <Card
                          mb='xxs'
                          backgroundColor='error/opacity'
                          key={item.metric?.name}
                          px='xs'
                          py='xxxs'
                          width='fit-content'
                        >
                          <Typography variant='p2/light' fontSize='xs'>
                            {item.metric?.name}
                          </Typography>
                        </Card>
                      );
                    }
                    return null;
                  })}
                </Box>
              </Card>
            </Box>
          </Box>
          <SurveyEmployeeResultsVsTeamVsDepartment
            campaignId={
              responseGetSurveysCampaigns?.findLast(
                campaign => campaign.surveyTemplateId === survey.templateId,
              )?.id
            }
          />
        </>
      ) : (
        <ResultsNotVisibleForEmployee templateName={survey.templateName} />
      )}
    </Box>
  );
};
